import React, { useCallback, useEffect } from "react";
import { FormLoginScheme } from "../../../Models/Schemas/form-login";
import { FORM_LOGIN } from "../../../Constants/Login";
import Button from "../../Elements/Button/Button";
import { setErrorFormSimpleUtil } from "../../../Utilities/Forms/errors";
import useForm from "../../../Hooks/useForm";
import Input from "../../Elements/Input/Input";
import Loader from "../../Elements/Loader/Loader";
import useStore from "../../../useStore";
import { useToast } from "../../../Contexts/Toast/toast.provider";
import "./login.css";
import useIsMobile from "../../../Hooks/useIsMobile";
import { Link, useNavigate } from "react-router-dom";

const Login = () => {
  const showToast = useToast();
  const {
    fetchDataLogin,
    fetchDataLoginGoogle,
    loading,
    error,
    success,
    resetError
  } = useStore();

  const isMobile = useIsMobile();

  const bgImage = isMobile
    ? "/assets/imgs/Backgroundmob.png"
    : "/assets/imgs/Backgroundprincipal-pc.png";

  const navigate = useNavigate();

  const handleCredentialResponse = useCallback(async (response) => {
    try {
      const idToken = response.credential;
      if (!idToken) return;

      const result = await fetchDataLoginGoogle(idToken);

      if (result?.statusCode === 200) {
        showToast("success", "¡Bienvenido con Google! 🎉");
        navigate("/");
      }
    } catch (err) {
      console.error("Error login Google:", err);
    }
  }, [fetchDataLoginGoogle, showToast, navigate]);


  useEffect(() => {
    /* global google */
    const initGoogleButton = () => {
      google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        callback: handleCredentialResponse,
      });
      google.accounts.id.renderButton(
        document.getElementById("googleBtn"),
        {
          theme: "outline",
          size: "large",
          text: "continue_with",
          shape: "pill",
          logo_alignment: "left",
          width: "100%",
          locale: "es",
        }
      );
    };

    // Si el script ya cargó, inicializa de inmediato
    if (window.google) {
      initGoogleButton();
      return;
    }

    // Si aún no cargó, espera en intervalos cortos hasta que esté disponible
    const interval = setInterval(() => {
      if (window.google) {
        clearInterval(interval);
        initGoogleButton();
      }
    }, 100);

    return () => clearInterval(interval);
  }, [handleCredentialResponse]);

  useEffect(() => {
    if (error) {
      showToast("error", error);
      resetError();
    }
  }, [error, showToast, resetError]);

  useEffect(() => {
    if (success) {
      showToast("success", "¡Bienvenido de nuevo! 🎉");
      resetError();
    }
  }, [success, showToast, resetError]);

  const { values, errors, touched, handleBlur, handleChange, isValid } =
    useForm(FORM_LOGIN, FormLoginScheme);

  const submit = () => {
    if (!isValid) return;
    fetchDataLogin(values);
  };

  if (loading) return <Loader />;

  return (
    <section
      className="login-bg d-flex align-items-center min-vh-100"
      style={{
        backgroundImage: `url(${bgImage})`,
        backgroundPosition: "center",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        width: "100%",
        height: "100%"
      }}
    >
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-12 col-sm-10 col-md-8 col-lg-6">
            <div className="card glass border-0 shadow-lg overflow-hidden">
              <div className="card-body p-4 p-md-5">
                <div className="text-center mb-4">
                  <img
                    src="assets/imgs/Sin fondo.png"
                    alt="BubUp"
                    className="login-logo mb-2"
                  />
                  <h4 className="fw-bold text-white mb-1">
                    Bienvenido a BubUp
                  </h4>
                  <p className="text-white-50 mb-0">
                    Vive más, encárganos lo demás.
                  </p>
                </div>
                <form onSubmit={(e) => e.preventDefault()}>
                  <div className="row g-3">
                    <div className="col-12">
                      <Input
                        mask=""
                        value={values.email}
                        label="Correo electrónico"
                        labelColor="white"   // 👈 aquí decides el color
                        onChange={handleChange("email")}
                        onBlur={handleBlur("email")}
                        error={setErrorFormSimpleUtil(
                          errors,
                          touched,
                          "email"
                        )}
                        success={
                          !setErrorFormSimpleUtil(
                            errors,
                            touched,
                            "email"
                          )
                        }
                      />
                    </div>
                    <div className="col-12">
                      <Input
                        value={values.password}
                        label="Contraseña"
                        labelColor="white"   // 👈 aquí decides el color
                        onChange={handleChange("password")}
                        onBlur={handleBlur("password")}
                        error={setErrorFormSimpleUtil(
                          errors,
                          touched,
                          "password"
                        )}
                        success={
                          !setErrorFormSimpleUtil(
                            errors,
                            touched,
                            "password"
                          )
                        }
                        variant="Password"
                      />
                    </div>

                    <div className="col-12 mt-2">
                      <div className="d-grid gap-2">
                        <Button
                          text="Entrar"
                          color="green"
                          fill
                          action={submit}
                          disabled={!isValid}
                          classNames="w-100 py-3"
                        />

                        <Button
                          text="Registrarse"
                          color="blue"
                          fill
                          action={() => (window.location.href = "/register")}
                          classNames="w-100 py-3"
                        />
                        <div className="">
                          <div id="googleBtn"></div>
                        </div>

                        {/* Links simples */}
                        <div className="row link-login">
                          <div className="col-6 text-start">
                            <Link
                              to="/forgot-password"
                              style={{ textDecoration: "none" }}
                            >
                              ¿Olvidaste tu contraseña?
                            </Link>
                          </div>

                          <div className="col-6">
                            <Link
                              to="/register/work-with-us"
                              style={{ textDecoration: "none" }}
                            >
                              ¿Quieres trabajar con nosotros?
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Login;